type SessionType = 'monthly' | 'default';

const TEST_SESSION_KEY = 'test-session';
const now = () => Math.floor(Date.now() / 1000);

interface SessionQuery {
  type?: SessionType;
  testId?: number;
}

export interface TestSession {
  type: SessionType;
  testId: number;
  attemptId: number;
  answers: Record<number, number[]>;
  questionOrder?: number[];
  expiresAt: number;
}

export const getTestSession = ({ type, testId }: SessionQuery = {}): TestSession | null => {
  const session = readStoredSession();
  if (!session || (type && session.type !== type) || (typeof testId === 'number' && session.testId !== testId)) {
    return null;
  }
  if (session.expiresAt <= now()) {
    deleteTestSession();
    return null;
  }
  return session;
};

export const updateTestSession = (payload: TestSession): void => {
  localStorage.setItem(TEST_SESSION_KEY, JSON.stringify(payload));
};

export const deleteTestSession = (): void => {
  localStorage.removeItem(TEST_SESSION_KEY);
};

const readStoredSession = (): TestSession | null => {
  try {
    const sessionInLocalStorage = localStorage.getItem(TEST_SESSION_KEY);
    if (!sessionInLocalStorage) return null;
    const parsedSession = JSON.parse(sessionInLocalStorage);
    if (!isValidSession(parsedSession)) {
      deleteTestSession();
      return null;
    }
    return parsedSession;
  } catch {
    return null;
  }
};

const isValidSession = (value: unknown): value is TestSession => {
  if (!value || typeof value !== 'object') return false;
  const d: Partial<TestSession> = value;
  return (
    (d.type === 'monthly' || d.type === 'default') &&
    typeof d.testId === 'number' &&
    typeof d.attemptId === 'number' &&
    typeof d.expiresAt === 'number' &&
    d.answers != null &&
    typeof d.answers === 'object' &&
    !Array.isArray(d.answers)
  );
};